import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  previousLabel?: string;
  nextLabel?: string;
  className?: string;
}

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  previousLabel = 'Vorige',
  nextLabel = 'Volgende',
  className,
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const visiblePages = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(page => {
      if (page === 1 || page === totalPages) return true;
      if (Math.abs(page - currentPage) <= 1) return true;
      return false;
    });

  return (
    <div className={cn('flex items-center justify-center gap-1 sm:gap-2 flex-wrap', className)}>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="flex items-center gap-1 px-2 sm:px-4 py-2 rounded-lg border border-border text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/40 hover:bg-card transition-all"
      >
        <ChevronLeft className="w-4 h-4" />
        <span className="hidden sm:inline">{previousLabel}</span>
      </button>

      <div className="flex items-center gap-1">
        {visiblePages.map((page, idx, arr) => (
          <span key={page} className="flex items-center">
            {idx > 0 && arr[idx - 1] !== page - 1 && (
              <span className="px-1 sm:px-2 text-muted-foreground">...</span>
            )}
            <button
              onClick={() => onPageChange(page)}
              className={cn(
                'w-8 h-8 sm:w-10 sm:h-10 rounded-lg text-sm font-medium transition-all',
                currentPage === page
                  ? 'bg-primary text-white'
                  : 'hover:bg-card border border-border hover:border-primary/40',
              )}
            >
              {page}
            </button>
          </span>
        ))}
      </div>

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="flex items-center gap-1 px-2 sm:px-4 py-2 rounded-lg border border-border text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/40 hover:bg-card transition-all"
      >
        <span className="hidden sm:inline">{nextLabel}</span>
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}
